// @ts-ignore
export class HTMLToMarkdownConverter {
  private turndown: any = null;
  private turndownPromise: Promise<any> | null = null;

  addCustomRules() {
    // 处理表格
    this.turndown.addRule('table', {
      filter: 'table',
      replacement (content: string, node: HTMLElement) {
        // console.log('first table', node);
        const rows = node.querySelectorAll('tr');
        let markdown = '';

        rows.forEach((row, index) => {
          const cells = row.querySelectorAll('th, td');
          let rowText = '|';

          cells.forEach((cell) => {
            rowText += ` ${cell.textContent.trim()} |`;
          });

          markdown += `${rowText  }\n`;

          // 添加表头分隔线
          if (index === 0) {
            markdown += `|${  ' --- |'.repeat(cells.length)  }\n`;
          }
        });

        return `${markdown  }\n`;
      },
    });

    // 处理图片
    this.turndown.addRule('images', {
      filter: 'img',
      replacement (content: string, node: HTMLImageElement) {
        const alt = node.alt || '';
        let src = node.src || '';
        const title = node.title ? ` "${node.title}"` : '';
        
        // 移除签名参数，保存原始 URL
        if (src?.includes('q-signature=')) {
          // 移除 q-signature 及其后面的所有参数
          [src] = src.split('?');
        }
        
        return `![${alt}](${src}${title})`;
      },
    });

    // 处理自定义 bid 标签：保持为原始 <bid>...</bid>
    this.turndown.addRule('bid', {
      filter: (node: HTMLElement) => node.nodeName.toLowerCase() === 'bid',
      replacement: (content: string, node: HTMLElement) => {
        // node.textContent 为 JSON 字符串
        const jsonText = (node.textContent || '').trim();
        return `\n<bid>${jsonText}</bid>\n`;
      },
    });
  }

  // 懒加载 turndown
  private async initTurndown() {
    if (this.turndown) return this.turndown;
    
    if (!this.turndownPromise) {
      this.turndownPromise = (async () => {
        const TurndownService = (await import('turndown')).default;
        this.turndown = new TurndownService({
          codeBlockStyle: 'fenced',
          headingStyle: 'atx',
        });
        this.addCustomRules();
        return this.turndown;
      })();
    }
    
    return this.turndownPromise;
  }

  // 同步方法：如果 turndown 未加载则返回空字符串
  // eslint-disable-next-line @typescript-eslint/member-ordering
  convert(html: string): string {
    if (!this.turndown) {
      return '';
    }
    try {
      const cleanedHtml = this.preprocessHTML(html);
      return this.turndown.turndown(cleanedHtml);
    } catch (error) {
      console.error('Conversion error:', error);
      return '';
    }
  }
  
  // 异步方法：推荐使用
  // eslint-disable-next-line @typescript-eslint/member-ordering
  async convertAsync(html: string): Promise<string> {
    try {
      await this.initTurndown();
      const cleanedHtml = this.preprocessHTML(html);
      return this.turndown.turndown(cleanedHtml);
    } catch (error) {
      console.error('Async conversion error:', error);
      return '';
    }
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  preprocessHTML(inputHtml: string): string {
    // 如果html为空或未定义，返回空字符串
    if (!inputHtml) return '';
    // 先恢复 bid 标记：将渲染时埋入的隐藏原文还原为 <bid>...<\/bid>
    try {
      const container = typeof window !== 'undefined' ? document.createElement('div') : null;
      let htmlStr = inputHtml;
      if (container) {
        container.innerHTML = htmlStr;
        
        // 移除 dba-table-actions 元素（表格操作按钮）
        const tableActions = container.querySelectorAll('.dba-table-actions');
        tableActions.forEach((el) => el.remove());
        
        const bidHolders = container.querySelectorAll('.dba-agent-bid');
        bidHolders.forEach((holder) => {
          const marker = holder.querySelector('.dba-agent-bid-text');
          const original = marker && (marker.textContent || marker.innerHTML);
          if (original) {
            const tmp = document.createElement('div');
            tmp.innerHTML = original as string;
            const nodes = Array.from(tmp.childNodes);
            (holder as any).replaceWith(...nodes);
          }
        });
        htmlStr = container.innerHTML;
      }
      // 移除不必要的标签和属性
      return htmlStr
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
        .replace(/ class="[^"]*"/g, '')
        .replace(/ style="[^"]*"/g, '');
    } catch (e) {
      // 忽略恢复异常，继续走默认清洗
    }
    // 移除不必要的标签和属性
    return (inputHtml as string)
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
      .replace(/ class="[^"]*"/g, '')
      .replace(/ style="[^"]*"/g, '');
  }
};

/**
 * 判断是否为已签名或 databrain 链接（旧版判断逻辑）
 */
export const isDataBrainUrl = (url: string): boolean => {
  if (!url) return false;
  const lower = url.toLowerCase().trim();
  // databrain 资源且未带签名参数，返回 true（需要签名）
  const isDataBrain = lower.includes('databrain');
  const alreadySigned = lower.includes('q-signature=');
  return isDataBrain && !alreadySigned;
};

/**
 * 腾讯云 COS 链接正则表达式
 * 匹配格式: https://<bucket-name>-<appid>.cos.<region>.myqcloud.com/...
 * 示例: https://databrain-test-1300342678.cos.ap-singapore.myqcloud.com/path/to/file
 */
const TENCENT_COS_URL_REGEX = /^https?:\/\/[\w-]+\.cos\.[a-z]+-[a-z]+\.myqcloud\.com\//i;

/**
 * 判断是否为腾讯云 COS 链接且需要签名
 * 精准匹配腾讯云 COS 域名格式：*.cos.<region>.myqcloud.com
 * @param url 待检测的 URL
 * @returns 如果是腾讯云 COS 链接且未签名，返回 true
 */
export const isTencentCosUrlNeedSign = (url: string): boolean => {
  if (!url) return false;
  const trimmedUrl = url.trim();
  
  // 检测是否为腾讯云 COS 链接格式
  const isCosUrl = TENCENT_COS_URL_REGEX.test(trimmedUrl);
  
  // COS 链接且未签名时返回 true（需要签名）
  return isCosUrl;
};

/**
 * 获取签名后的链接 URL
 * 对于腾讯云 COS 链接进行签名，其他链接直接返回原始 URL
 * @param href 原始链接地址
 * @param getSignedUrl 签名函数（来自 useImageCacheLocal hook）
 * @param forceSign 是否强制签名，为 true 时跳过 URL 判断直接签名
 * @returns 签名后的链接 URL
 */
export const getSignedLinkUrl = async (
  href: string | undefined,
  getSignedUrl: (url: string, expiryTime?: number, forceSign?: boolean) => Promise<string>,
  forceSign?: boolean
): Promise<string | null> => {
  if (!href) return null;
  
  let finalUrl = href;
  
  // 如果是腾讯云 COS 链接或强制签名，进行签名
  if (forceSign || isTencentCosUrlNeedSign(href)) {
    try {
      finalUrl = await getSignedUrl(href, undefined, forceSign);
    } catch (error) {
      console.warn('Failed to sign link URL:', href, error);
      // 签名失败时使用原始 URL
      finalUrl = href;
    }
  }
  
  return finalUrl;
};

/**
 * 检查删除操作是否会影响到受保护元素
 * @param {Range} range - 当前选区范围
 * @param {HTMLElement} protectedElement - 受保护的元素（如 `.dba-agent-chart-container`）
 * @param {string} key - 按下的键（'Backspace' 或 'Delete'）
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function isDeleteAffectingElement(range: Range, protectedElement: HTMLElement | null, key: string) {
  if (!protectedElement) return false;
  // 情况1：选区在受保护元素内
  if (protectedElement.contains(range.startContainer)) {
    return true;
  }
  // 情况2：Backspace（光标在受保护元素后面）
  // if (key === 'Backspace') {
  //   const nodeBefore =range.endOffset === 0 && range.startContainer.nodeType === Node.TEXT_NODE;
  //   return nodeBefore;
  // }
  return false;
}
/**
 * 判断 conversations 中是否包含海报图片文档（png_file）
 *
 * @param data       conversations 数组
 * @param latestOnly 为 true 时仅检查最后一轮对话，为 false 时遍历所有历史
 */
export function hasPosterDocument(data: unknown[], latestOnly = false): boolean {
  if (!Array.isArray(data) || data.length === 0) return false;

  const check = (conv: any) =>
    Array.isArray(conv?.answer) &&
    conv.answer.some(
      (item: any) =>
        item.type === 'document' && item.value?.file_suffix === 'png_file',
    );

  return latestOnly ? check(data[data.length - 1]) : data.some(check);
}

/**
 * 提取 conversations 中「最新生成的海报图片(png_file)」的 cos_url。
 *
 * 用途：左侧海报预览面板据此复用主对话框已生成的同一张海报图片
 *（与正文卡片同源、共享同一签名缓存），避免再次走 get_excel_info 拉取文件列表 + 签名
 * 带来的左右不一致与不稳定问题。
 *
 * @param conversations conversations 数组
 * @returns 最新一条 png_file 文档的 cos_url；未找到返回 ''
 */
export function getLatestPosterCosUrl(conversations: unknown[]): string {
  if (!Array.isArray(conversations) || conversations.length === 0) return '';
  // 从最新一轮往前找；同一轮内取最后一个 png 文档（最新）
  for (let i = conversations.length - 1;i >= 0;i -= 1) {
    const answer = (conversations[i] as any)?.answer;
    if (!Array.isArray(answer)) continue;
    for (let j = answer.length - 1;j >= 0;j -= 1) {
      const item = answer[j];
      if (item?.type === 'document' && item?.value?.file_suffix === 'png_file') {
        const cosUrl = item.value?.cos_url || item.value?.url || '';
        if (cosUrl) return cosUrl;
      }
    }
  }
  return '';
}

/**
 * 根据文件 id 在 conversations 中查找对应 conversation 的 frontend_metadata
 *
 * @param conversations conversations 数组
 * @param fileId        目标文件 id
 * @returns 对应 conversation 的 frontend_metadata，未找到则返回 undefined
 */
export function findFrontendMetadataByFileId(
  conversations: unknown[],
  fileId: string | undefined,
): Record<string, any> | undefined {
  if (!Array.isArray(conversations) || !fileId) return undefined;
  return conversations.find(task => task?.messageId === fileId)?.frontend_metadata;
}

/**
 * 从 dsReport.frontend_metadata 解析出海报风格默认值
 */
export function computeStyleFromMetadata(
  metadata: Record<string, any> | undefined,
  query?: string,
): { template: any; customInput: string; } {
  const {
    templateId, poster_template: posterTemplate, query: metadataQuery,
  } = metadata ?? {};
  
  // 0 - 默认(default)：用户没选模板
  // 1 - 预设(preset)：用户选了预设模版
  // 2 - 自定义(custom)：用户在md文件页面，手动输入了风格query
  if (posterTemplate === 0 || posterTemplate === 2) {
    return {
      template: { id: 'custom', isCustom: true },
      // 优先使用 metadata 中保存的 query（重新打开面板时恢复），否则使用传入的 query
      customInput: metadataQuery ?? query ?? '',
    };
  }
  if (posterTemplate === 1 && templateId) {
    return {
      template: { id: templateId },
      customInput: '',
    };
  }
  return { template: null, customInput: '' };
}

/**
 * 生成右侧面板的 React key。
 *
 * 正文 <cos> 标签卡片是只读预览，同一会话内多张卡片各自对应不同文件，
 * 但面板组件保持挂载时会沿用上一个文件的内部状态（已选文件、工作表、签名地址缓存），
 * 导致点击第二张卡片仍展示第一张的数据。以 document_id + cos_url 作为 key 强制重新挂载，
 * 保证点哪张卡片就重新请求哪张卡片的数据。
 *
 * 真正表格（aiExcel）返回 undefined，不参与重挂载，编辑态与自动保存流程保持不变。
 *
 * @param dsReport - 当前激活的报告数据
 * @returns cos 只读预览场景下的 key，其余场景返回 undefined
 */
export function getCosReportPanelKey(
  dsReport: Record<string, any> | null | undefined,
): string | undefined {
  if (!dsReport?.isCosTag) return undefined;
  const documentId = dsReport?.document_id || dsReport?.id || '';
  const cosUrl = dsReport?.cos_url || dsReport?.url || '';
  return `cos-${documentId}-${cosUrl}`;
}